import { memo } from 'react';
import { Box, Button, Flex, Menu } from '@chakra-ui/react';
import { CaretDown } from '@phosphor-icons/react';
import { Controller, Control } from 'react-hook-form';
import { ITimezoneOption } from 'react-timezone-select';

import { Field } from '@/components/ui/field';
import { FormValues } from '../hooks/useSessionForm';

const ErrorMessage = ({ message }: { message?: string }) =>
  message ? (
    <Box fontSize="14px" color="error.700" role="alert">
      {message}
    </Box>
  ) : null;

interface TimezoneDropdownProps {
  control: Control<FormValues>;
  error?: { message?: string };
  isSubmitting: boolean;
  isLoadingCandidate: boolean;
  options: { value: string; label: string }[];
  parseTimezone: (timezone: string) => ITimezoneOption;
}

export const TimezoneDropdown: React.FC<TimezoneDropdownProps> = memo(
  ({ control, error, isSubmitting, isLoadingCandidate, options, parseTimezone }) => (
    <Field
      flex="1"
      label={
        <Box fontSize="16px" fontWeight="semibold">
          Timezone
        </Box>
      }
      invalid={!!error}
      errorText={<ErrorMessage message={error?.message} />}
    >
      <Controller
        name="timezone"
        control={control}
        rules={{ required: 'Timezone is required' }}
        render={({ field }) => (
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                variant="outline"
                size="lg"
                borderColor={error ? 'error.500' : 'neutrals.200'}
                borderRadius="8px"
                disabled={isSubmitting || isLoadingCandidate}
                _disabled={{
                  bg: 'neutrals.300'
                }}
                w="280px"
                aria-label="Select timezone"
              >
                <Flex justifyContent="space-between" alignItems="center" w="100%">
                  <Box
                    fontSize="16px"
                    color="neutrals.700"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                  >
                    {field.value?.value?.label || 'Select timezone'}
                  </Box>
                  <Box as="span" ml="8px" mr="0">
                    <CaretDown size={16} aria-hidden="true" />
                  </Box>
                </Flex>
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content minW="10rem" maxH="300px" overflowY="auto">
                <Menu.RadioItemGroup
                  value={field.value?.value.value || ''}
                  onValueChange={(value) => {
                    const selectedTimezone = parseTimezone(value.value);
                    field.onChange({ value: selectedTimezone });
                  }}
                >
                  {options.map(({ value, label }) => (
                    <Menu.RadioItem
                      key={value}
                      value={value}
                      fontSize="16px"
                      _hover={{
                        cursor: 'pointer',
                        bg: 'neutrals.100'
                      }}
                    >
                      {label}
                    </Menu.RadioItem>
                  ))}
                </Menu.RadioItemGroup>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}
      />
    </Field>
  )
);

TimezoneDropdown.displayName = 'TimezoneDropdown';
